import React, { FormEvent, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearAuthTokens, isAuthenticated, login } from '../services/auth';
import '../styles/admin-login.css';

export const AdminLoginPage: React.FC = () => {
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isAuthenticated()) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await login(email.trim(), password);
      navigate('/admin', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('loginError'));
      clearAuthTokens();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-wrapper">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <div className="admin-login-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h1 className="admin-login-title">{t('loginTitle')}</h1>
            <p className="admin-login-subtitle">{t('loginSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="admin-login-form">
            <div className="admin-login-field">
              <label htmlFor="admin-email" className="admin-login-label">
                {t('email')}
              </label>
              <input
                id="admin-email"
                type="email"
                className="admin-login-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                required
                autoComplete="username"
              />
            </div>

            <div className="admin-login-field">
              <label htmlFor="admin-password" className="admin-login-label">
                {t('password')}
              </label>
              <input
                id="admin-password"
                type="password"
                className="admin-login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>

            {error && <div className="admin-login-error">{error}</div>}

            <button type="submit" className="admin-login-submit" disabled={loading}>
              {loading ? (
                <>
                  <svg className="admin-login-spinner" viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                      strokeDasharray="31.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  {t('signingIn')}
                </>
              ) : (
                t('signIn')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
